import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react';
import clientEngagements from '../data/clientEngagements.json';

// Stage -> ticker glyph/color, styled after a stock exchange board
// (green/up for closed-won, red/down for at-risk, amber for in-progress).
const STAGE_STYLE = {
  'Sold Deal':             { glyph: '▲', color: '#00c805' },
  'In-Flight Deal':        { glyph: '►', color: '#f0b90b' },
  'Pre-Sales':             { glyph: '●', color: '#3ea6ff' },
  'Break-Fix/Post-Sales':  { glyph: '▼', color: '#ff3b3b' },
};

// Funnel order used when the Stage column is sorted, rather than alphabetical.
const STAGE_ORDER = ['Pre-Sales', 'In-Flight Deal', 'Sold Deal', 'Break-Fix/Post-Sales'];

const styles = {
  board: {
    background: '#fff',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    padding: '12px 0',
    overflow: 'hidden',
    width: '100%',
    cursor: 'pointer',
  },
  viewport: {
    overflow: 'hidden',
    width: '100%',
    WebkitMaskImage: 'linear-gradient(90deg, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%)',
    maskImage: 'linear-gradient(90deg, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%)',
  },
  item: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: '8px',
    fontFamily: "'Courier New', Courier, monospace",
    whiteSpace: 'nowrap',
  },
  client: { color: '#1a1a1a', fontWeight: '700', fontSize: '13px', letterSpacing: '0.3px' },
  usecase: { color: '#888', fontSize: '12px' },
  stage: { fontWeight: '700', fontSize: '12px', letterSpacing: '0.3px' },
  sep: { color: '#ddd', margin: '0 20px' },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modal: { background: '#0a0a0a', borderRadius: '12px', padding: '24px', maxWidth: '760px', width: '100%', maxHeight: '80vh', overflowY: 'auto', position: 'relative', border: '1px solid #222' },
  modalClose: { position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', color: '#8a8a8a', display: 'flex' },
  modalTitle: { fontSize: '15px', fontWeight: '700', color: '#f5f5f5', marginBottom: '2px', fontFamily: "'Courier New', Courier, monospace" },
  modalSub: { fontSize: '12px', color: '#8a8a8a', marginBottom: '18px', fontFamily: "'Courier New', Courier, monospace" },

  table: { width: '100%', borderCollapse: 'collapse', fontFamily: "'Courier New', Courier, monospace" },
  th: { textAlign: 'left', fontSize: '11px', color: '#8a8a8a', letterSpacing: '0.05em', padding: '8px 12px', borderBottom: '1px solid #222', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' },
  thInner: { display: 'inline-flex', alignItems: 'center', gap: '4px' },
  td: { padding: '10px 12px', borderBottom: '1px solid #161616', fontSize: '13px' },
};

const tickerCSS = `
  .ClientTickerTrack {
    display: flex;
    align-items: center;
    width: max-content;
    animation: ClientTickerScroll 110s linear infinite;
  }
  .ClientTickerTrack:hover {
    animation-play-state: paused;
  }
  @keyframes ClientTickerScroll {
    from { transform: translateX(0); }
    to   { transform: translateX(-50%); }
  }
  @media (prefers-reduced-motion: reduce) {
    .ClientTickerTrack { animation: none; }
  }
  .ClientTickerRow:hover {
    background: #141414;
  }
`;

function TickerItem({ client, usecase, stage }) {
  const { glyph, color } = STAGE_STYLE[stage] || { glyph: '●', color: '#8a8a8a' };
  return (
    <span style={styles.item}>
      <span style={styles.client}>{client}</span>
      <span style={styles.usecase}>{usecase}</span>
      <span style={{ ...styles.stage, color }}>{glyph} {stage.toUpperCase()}</span>
    </span>
  );
}

const COLUMNS = [
  { key: 'client',  label: 'Client' },
  { key: 'usecase', label: 'Use Case' },
  { key: 'stage',   label: 'Stage' },
];

function sortRows(rows, sortKey, sortDir) {
  if (!sortKey) return rows;
  const dir = sortDir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    if (sortKey === 'stage') {
      return (STAGE_ORDER.indexOf(a.stage) - STAGE_ORDER.indexOf(b.stage)) * dir;
    }
    return a[sortKey].localeCompare(b[sortKey]) * dir;
  });
}

function EngagementTableModal({ onClose }) {
  const [sortKey, setSortKey] = useState('client');
  const [sortDir, setSortDir] = useState('asc');

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const rows = sortRows(clientEngagements, sortKey, sortDir);

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <button style={styles.modalClose} onClick={onClose}><X size={18} /></button>
        <div style={styles.modalTitle}>Client Engagements</div>
        <div style={styles.modalSub}>{clientEngagements.length} accounts · click a column to sort</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                {COLUMNS.map(({ key, label }) => {
                  const active = sortKey === key;
                  const Icon = active ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ChevronsUpDown;
                  return (
                    <th key={key} style={{ ...styles.th, color: active ? '#f5f5f5' : styles.th.color }} onClick={() => toggleSort(key)}>
                      <span style={styles.thInner}>{label} <Icon size={12} /></span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const { glyph, color } = STAGE_STYLE[row.stage] || { glyph: '●', color: '#8a8a8a' };
                return (
                  <tr key={`${row.client}-${i}`} className="ClientTickerRow">
                    <td style={{ ...styles.td, color: '#f5f5f5', fontWeight: '700' }}>{row.client}</td>
                    <td style={{ ...styles.td, color: '#8a8a8a' }}>{row.usecase}</td>
                    <td style={{ ...styles.td, color, fontWeight: '700' }}>{glyph} {row.stage.toUpperCase()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ClientEngagementTicker() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div
        style={styles.board}
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        aria-label="View all client engagements"
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setOpen(true)}
      >
        <style>{tickerCSS}</style>
        <div style={styles.viewport}>
          <div className="ClientTickerTrack">
            {clientEngagements.map((row, i) => (
              <React.Fragment key={i}>
                <TickerItem {...row} />
                <span style={styles.sep}>•</span>
              </React.Fragment>
            ))}
            {/* Duplicate, hidden from assistive tech — makes the scroll loop seamless */}
            {clientEngagements.map((row, i) => (
              <React.Fragment key={`dup-${i}`}>
                <TickerItem {...row} />
                <span style={styles.sep} aria-hidden="true">•</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {open && createPortal(<EngagementTableModal onClose={() => setOpen(false)} />, document.body)}
    </>
  );
}

export default ClientEngagementTicker;
